import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import ActionButton from '../../../../../Components/Common/ActionButton';

import * as api from '../planningApi';

const INFO_TEXT =
  'Registered Supplier list is generated from the Registered Supplier List master data with parameters ' +
  'Crude & Condensate as Cargo Category, Supplier, Tender Invitation, and the ' +
  "document's Contract Type (Term/Spot) as saved in Main Requirement. For " +
  'Pelelangan Khusus, ALL generated Registered Supplier must be invited. The list will be ' +
  'attached to the memo or Nota Dinas Izin Refinery and Hydrocarbon Procurement.';

const SLIDER_LABEL =
  'The list of Business Partners displayed in the Registered Supplier table are Business ' +
  'Partners who will be invited and/or accept the invitation, and will be ' +
  'attached to the memo or Nota Dinas Izin Refinery dan Hydrocarbon Procurement';

// DMUT Invitation tab. Generate is a LIVE call to eRS (section 19 — needs the
// Pertamina internal network / VPN; a 422 "Gagal mengambil data DMUT..." off-net
// is expected, not a bug). It is transient: the checked rows are persisted only
// when the parent's Save / Save & Completed runs (section 21, replace-total).
const DmutInvitationTab = ({
  planningId,
  detail,
  setDetail,
  readOnly = false,
}) => {
  const dmut = detail.dmut;
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const setDmut = patch =>
    setDetail(prev => ({ ...prev, dmut: { ...prev.dmut, ...patch } }));

  const handleGenerate = async () => {
    // Hasil generate difilter server-side berdasarkan Contract Type TERSIMPAN
    // (Term/Spot) — pastikan sudah dipilih & disimpan di tab Requirement dulu,
    // supaya daftar yang keluar sesuai dokumen.
    if (!detail.requirement?.contractType) {
      toast.error(
        'Pilih Contract Type (Term/Spot) di tab Requirement dan simpan terlebih dahulu — Registered Supplier list difilter berdasarkan Contract Type dokumen.'
      );
      return;
    }
    setGenerating(true);
    try {
      const items = await api.generateDmutInvitation(planningId);
      // Pelelangan Khusus mewajibkan SEMUA DMUT hasil generate diundang —
      // semua baris langsung tercentang (validasi juga menuntut semuanya).
      const rows = items.map(it => ({
        dmutCode: it.dmutCode,
        dmutName: it.dmutName,
        dmutCargoCategory: it.dmutCargoCategory,
        dmutCountry: it.dmutCountry,
        pics: it.pics || [],
        invited: true,
      }));
      setDmut({ generated: true, rows });
      toast.success(
        `${rows.length} Registered Supplier generated — all invited (required)`
      );
    } catch (error) {
      toast.error(`Generate failed: ${error}`);
    } finally {
      setGenerating(false);
    }
  };

  const toggleInvite = code =>
    setDmut({
      rows: dmut.rows.map(r =>
        r.dmutCode === code ? { ...r, invited: !r.invited } : r
      ),
    });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await api.downloadDmutInvitation(planningId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DMUT_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Download failed: ${error}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <Alert color='info' className='d-flex'>
        <i className='ri-information-line fs-16 me-2'></i>
        <span>{INFO_TEXT}</span>
      </Alert>

      <Card className='border shadow-none'>
        <CardBody>
          <div className='form-check form-switch mb-3'>
            <Input
              type='checkbox'
              role='switch'
              className='form-check-input'
              id='dmut-confirm'
              checked={!!dmut.confirmed}
              disabled={readOnly}
              onChange={e => setDmut({ confirmed: e.target.checked })}
            />
            <Label for='dmut-confirm' className='form-check-label'>
              {SLIDER_LABEL}
            </Label>
          </div>

          <div className='d-flex gap-2 flex-wrap'>
            {!readOnly && (
              <ActionButton
                variant='primary'
                icon='ri-refresh-line'
                disabled={!dmut.confirmed}
                loading={generating}
                onClick={handleGenerate}
              >
                Generate
              </ActionButton>
            )}
            <ActionButton
              variant='secondary'
              icon='ri-file-excel-2-line'
              loading={downloading}
              onClick={handleDownload}
            >
              Download Registered Supplier
            </ActionButton>
          </div>
        </CardBody>
      </Card>

      <Card className='border shadow-none'>
        <CardHeader className='fw-semibold bg-light'>
          Registered Supplier List
        </CardHeader>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle table-nowrap mb-0'>
              <thead className='table-light'>
                <tr>
                  {/* DMUT Code & Country disembunyikan dari tampilan (code kini
                      berupa GUID internal) — datanya tetap ikut tersimpan. */}
                  <th className='text-center' style={{ width: 60 }}>
                    Invite
                  </th>
                  <th>Registered Supplier Name</th>
                  <th>PIC Name</th>
                  <th>PIC Email</th>
                </tr>
              </thead>
              <tbody>
                {!dmut.rows.length ? (
                  <tr>
                    <td colSpan={4} className='text-center text-muted py-3'>
                      No Registered Supplier generated yet
                    </td>
                  </tr>
                ) : (
                  dmut.rows.map(r => (
                    <tr key={r.dmutCode}>
                      <td className='text-center'>
                        <Input
                          type='checkbox'
                          className='form-check-input'
                          checked={!!r.invited}
                          disabled={readOnly}
                          onChange={() => toggleInvite(r.dmutCode)}
                        />
                      </td>
                      <td>{r.dmutName}</td>
                      <td>
                        {(r.pics || []).map(p => p.picName).join(', ') || '-'}
                      </td>
                      <td>
                        {(r.pics || []).map(p => p.picEmail).join(', ') || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default DmutInvitationTab;
